import { useEffect, useRef, useState } from "react";
import { ImageIcon } from "lucide-react";
import { api } from "../lib/api";

const cache = new Map(); // `${kind}/${id}` -> data URL
const inflight = new Map();

export async function loadPhoto(kind, id) {
  const key = `${kind}/${id}`;
  if (cache.has(key)) return cache.get(key);
  if (!inflight.has(key)) {
    inflight.set(
      key,
      api
        .get(`/photos/${kind}/${id}`)
        .then((r) => {
          cache.set(key, r.data.photo_b64);
          return r.data.photo_b64;
        })
        .finally(() => inflight.delete(key))
    );
  }
  return inflight.get(key);
}

// Thumbnail that fetches its image only when scrolled into view. Lists stay light; photos load on demand.
export function LazyPhoto({ kind, id, alt = "Photo", className = "", onOpen, title, testid }) {
  const ref = useRef(null);
  const [src, setSrc] = useState(() => cache.get(`${kind}/${id}`) || "");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (src || !ref.current) return;
    let cancelled = false;
    const start = () => loadPhoto(kind, id).then((u) => !cancelled && setSrc(u)).catch(() => !cancelled && setFailed(true));
    if (!("IntersectionObserver" in window)) {
      start();
      return () => { cancelled = true; };
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        start();
      }
    }, { rootMargin: "200px" });
    io.observe(ref.current);
    return () => { cancelled = true; io.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id]);

  const open = async () => {
    if (!onOpen) return;
    const url = src || (await loadPhoto(kind, id).catch(() => ""));
    if (url) onOpen(url, title);
  };

  return (
    <button type="button" ref={ref} onClick={open} className={`block overflow-hidden ${className}`} title={title || alt} data-testid={testid}>
      {src ? (
        <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <span className={`w-full h-full grid place-items-center bg-[#F2EDE4] text-[#8A8577] ${failed ? "" : "animate-pulse"}`}>
          <ImageIcon className="w-4 h-4" />
        </span>
      )}
    </button>
  );
}
